// ===========================================
// 💸 COMPONENTE DE GASTOS MODERNO: src/components/AGTFin/dashboard/expenses/FinExpense.js
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Paper,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  DirectionsCar as DirectionsCarIcon,
  LocalCafe as LocalCafeIcon,
  School as SchoolIcon,
  LocalHospital as LocalHospitalIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
  CalendarToday as CalendarTodayIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import agtFinService from '../../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES MODERNA (IDÉNTICA A FinBill)
// ===========================================
const getGrayscalePalette = (theme) => ({
  // Colores principales en escala de grises
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  
  // Gradientes para elementos visuales
  gradients: {
    primary: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    
    tertiary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #BDBDBD 0%, #9E9E9E 100%)'
      : 'linear-gradient(180deg, #616161 0%, #757575 100%)',
    
    card: theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,1) 100%)'
  },
  
  // Colores para categorías
  categories: {
    food: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
    transportation: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
    entertainment: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
    shopping: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
    healthcare: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
    education: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
    other: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161'
  },
  
  // Opacidades para diferentes elementos
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
    dark: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
    card: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'
  }
});

// ===========================================
// 🎯 COMPONENTES ESTILIZADOS MODERNOS (BASADO EN FinBill)
// ===========================================

// Contenedor principal moderno
const ModernContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0,0,0,0.2)' 
    : 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(255,255,255,0.1)' 
    : '1px solid rgba(0,0,0,0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 16,
  }
}));

// Tarjeta de resumen moderna
const SummaryCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.4)'
        : '0 12px 40px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.primary,
    }
  };
});

// Tarjeta de gasto moderna
const ModernExpenseCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);

  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.2)'
      : '0 8px 32px rgba(0,0,0,0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: theme.spacing(2),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.3)'
        : '0 12px 40px rgba(0,0,0,0.12)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.secondary,
    }
  };
});

// Botón moderno con estilo del dashboard
const ModernButton = styled(Button)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 16,
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    background: colors.gradients.primary,
    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    border: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(255,255,255,0.1)'
      : '0 4px 16px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(255,255,255,0.15)'
        : '0 8px 24px rgba(0,0,0,0.15)',
      background: colors.gradients.secondary,
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
    '&:disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
    }
  };
});

// Diálogo moderno
const ModernDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 24,
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(30,30,30,0.95)'
          : 'rgba(255,255,255,0.95)'),
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: isWallpaperMode
        ? '1px solid rgba(255, 255, 255, 0.2)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.05)'),
      boxShadow: 'none',
    }
  };
});

// Campo de texto moderno
const ModernTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'noborder'
})(({ theme, noborder }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      ...(noborder && {
        border: 'none',
        '& fieldset': {
          border: 'none',
        },
        '&:hover fieldset': {
          border: 'none',
        },
        '&.Mui-focused fieldset': {
          border: 'none',
        },
        '&:hover': {
          background: colors.backgrounds.light,
        },
        '&.Mui-focused': {
          background: colors.backgrounds.light,
        }
      }),
      ...(!noborder && {
        '&:hover': {
          background: colors.backgrounds.medium,
        },
        '&.Mui-focused': {
          background: colors.backgrounds.medium,
          '& fieldset': {
            borderColor: colors.primary,
            borderWidth: 2,
          }
        },
        '& fieldset': {
          borderColor: colors.backgrounds.dark,
          transition: 'all 0.3s ease',
        }
      })
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        fontSize: '16px', // Evita zoom en iOS
      }
    }
  };
});

// FormControl moderno
const ModernFormControl = styled(FormControl)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.backgrounds.medium,
      },
      '&.Mui-focused': {
        background: colors.backgrounds.medium,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: 2,
        }
      },
      '& fieldset': {
        borderColor: colors.backgrounds.dark,
        transition: 'all 0.3s ease',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  };
});

// Chip moderno con gradiente
const ModernChip = styled(Chip)(({ theme, chiptype }) => {
  const colors = getGrayscalePalette(theme);
  
  const getChipStyles = () => {
    switch (chiptype) {
      case 'primary':
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'secondary':
        return {
          background: colors.gradients.secondary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'tertiary':
        return {
          background: colors.gradients.tertiary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      default:
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
    }
  };

  return {
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '12px',
    height: 28,
    textTransform: 'uppercase',
    ...getChipStyles(),
    '& .MuiChip-icon': {
      color: 'inherit',
    }
  };
});

// ===========================================
// 📝 CONSTANTES Y CONFIGURACIÓN
// ===========================================

const EXPENSE_CATEGORIES = [
  { value: 'food', labelKey: 'agtfin.expenses.categoryTypes.food', icon: <LocalCafeIcon /> },
  { value: 'transportation', labelKey: 'agtfin.expenses.categoryTypes.transportation', icon: <DirectionsCarIcon /> },
  { value: 'entertainment', labelKey: 'agtfin.expenses.categoryTypes.entertainment', icon: <LocalCafeIcon /> },
  { value: 'shopping', labelKey: 'agtfin.expenses.categoryTypes.shopping', icon: <ShoppingCartIcon /> },
  { value: 'healthcare', labelKey: 'agtfin.expenses.categoryTypes.healthcare', icon: <LocalHospitalIcon /> },
  { value: 'education', labelKey: 'agtfin.expenses.categoryTypes.education', icon: <SchoolIcon /> },
  { value: 'other', labelKey: 'agtfin.expenses.categoryTypes.other', icon: <CategoryIcon /> }
];

const PAYMENT_METHODS = [
  { value: 'cash', labelKey: 'agtfin.expenses.paymentMethods.cash' },
  { value: 'debit_card', labelKey: 'agtfin.expenses.paymentMethods.debit' },
  { value: 'credit_card', labelKey: 'agtfin.expenses.paymentMethods.credit' },
  { value: 'transfer', labelKey: 'agtfin.expenses.paymentMethods.transfer' },
  { value: 'other', labelKey: 'agtfin.expenses.paymentMethods.other' }
];

// ===========================================
// 📊 SECCIÓN DE RESUMEN MEJORADA
// ===========================================
const SummarySection = ({ summary, userProfile, theme, t }) => {
  const colors = getGrayscalePalette(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  const totalExpenses = summary.total || 0;
  const expenseCount = summary.count || 0;
  const avgExpense = expenseCount > 0 ? totalExpenses / expenseCount : 0;

  const summaryItems = [
    {
      title: t('agtfin.expenses.totals'),
      value: totalExpenses,
      icon: <TrendingDownIcon />,
      gradient: colors.gradients.primary,
      type: 'primary'
    },
    {
      title: t('agtfin.expenses.registered'),
      value: expenseCount,
      icon: <ReceiptIcon />,
      gradient: colors.gradients.secondary,
      type: 'secondary',
      isCount: true
    },
    {
      title: t('agtfin.expenses.averagePerExpense'),
      value: avgExpense,
      icon: <AnalyticsIcon />,
      gradient: colors.gradients.tertiary,
      type: 'tertiary'
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
        {t('agtfin.expenses.summary')}
      </Typography>
      
      <Grid container spacing={isMobile ? 2 : 3}>
        {summaryItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <SummaryCard>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    background: item.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon, { 
                      sx: { 
                        fontSize: 28, 
                        color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                      } 
                    })}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight={700} sx={{ color: colors.primary }}>
                      {item.isCount ? item.value : formatCurrency(item.value)}
                    </Typography>
                  </Box>
                </Box>
                {item.isCount && (
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {item.value === 1 ? t('agtfin.expenses.expenseRegistered') : t('agtfin.expenses.expensesRegistered')}
                  </Typography>
                )}
              </CardContent>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ===========================================
// 📊 SECCIÓN DE CATEGORÍAS
// ===========================================
const CategorySummarySection = ({ summary, theme, t }) => {
  const colors = getGrayscalePalette(theme);
  
  if (!summary.categories || Object.keys(summary.categories).length === 0) {
    return null;
  }

  const formatCurrency = (amount, currency = 'USD') => {
    return agtFinService.formatCurrency(amount || 0, currency);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
        {t('agtfin.expenses.byCategory')}
      </Typography>
      
      <SummaryCard>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {Object.entries(summary.categories).map(([category, data]) => {
              const categoryInfo = EXPENSE_CATEGORIES.find(cat => cat.value === category);
              const percentage = summary.total > 0 ? (data.total / summary.total) * 100 : 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Box sx={{ 
                    p: 3, 
                    borderRadius: 4,
                    background: colors.backgrounds.light,
                    border: `1px solid ${colors.backgrounds.medium}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: colors.backgrounds.medium,
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        background: colors.gradients.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {React.cloneElement(categoryInfo?.icon || <CategoryIcon />, { 
                          sx: { 
                            fontSize: 20, 
                            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                          } 
                        })}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.primary }}>
                          {categoryInfo ? t(categoryInfo.labelKey) : category}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.count} {data.count === 1 ? t('agtfin.expenses.expense') : t('agtfin.expenses.expenses')}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="h6" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                      {formatCurrency(data.total)}
                    </Typography>
                    
                    <Box sx={{ 
                      height: '4px', 
                      backgroundColor: colors.backgrounds.medium,
                      borderRadius: '2px',
                      overflow: 'hidden',
                      mb: 1
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${percentage}%`,
                        background: colors.gradients.primary,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {percentage.toFixed(1)}% {t('agtfin.expenses.ofTotal')}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </SummaryCard>
    </Box>
  );
};

// ===========================================
// 💸 COMPONENTE PRINCIPAL MODERNO
// ===========================================
const FinExpense = ({ user, userProfile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = getGrayscalePalette(theme);
  
  // Estados principales
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados de modales
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // Estados de filtrado
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });
  // const [showFilters, setShowFilters] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    category: 'other',
    description: '',
    paymentMethod: 'debit_card',
    isEssential: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Cargar gastos y resumen
  const loadExpensesData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔄 Cargando datos de gastos para usuario:', user?.uid);
      
      if (!agtFinService || !agtFinService.apiClient) {
        throw new Error(t('agtfin.expenses.messages.serviceNotAvailable'));
      }
      
      const options = {
        startDate: format(dateRange.start, 'yyyy-MM-dd'),
        endDate: format(dateRange.end, 'yyyy-MM-dd'),
        limit: 50
      };

      if (selectedCategory) {
        options.category = selectedCategory;
      }

      const [expensesResponse, summaryResponse] = await Promise.all([
        agtFinService.apiClient.get('/agtfin/expenses', { params: options }),
        agtFinService.apiClient.get('/agtfin/expenses/summary', { params: options })
      ]);
      
      console.log('✅ Datos de gastos recibidos:', { expensesResponse, summaryResponse });
      
      setExpenses(expensesResponse.data.expenses || []);
      setSummary(summaryResponse.data.summary || {});
      
    } catch (err) {
      console.error('❌ Error cargando datos de gastos:', err);

      let errorMessage = t('agtfin.expenses.messages.loadError');

      if (err.message.includes('apiClient')) {
        errorMessage = t('agtfin.expenses.messages.serviceError');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.expenses.messages.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, dateRange, selectedCategory]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.uid) {
      loadExpensesData();
    } else {
      setLoading(false);
      setError(t('agtfin.expenses.messages.userNotAvailable'));
    }
  }, [user?.uid, loadExpensesData]);

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('agtfin.expenses.messages.nameRequired');
    }

    if (!formData.amount) {
      errors.amount = t('agtfin.expenses.messages.amountRequired');
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = t('agtfin.expenses.messages.amountPositive');
    }

    if (!formData.date) {
      errors.date = t('agtfin.expenses.messages.dateRequired');
    }

    return errors;
  };

  // Crear/actualizar gasto
  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const expenseData = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        date: formData.date,
        category: formData.category,
        description: formData.description?.trim() || '',
        paymentMethod: formData.paymentMethod,
        isEssential: Boolean(formData.isEssential)
      };
      
      if (editingExpense) {
        console.log('🔄 Actualizando gasto:', editingExpense.expenseId, expenseData);
        await agtFinService.apiClient.put(`/agtfin/expenses/${editingExpense.expenseId}`, expenseData);
      } else {
        console.log('💾 Creando nuevo gasto:', expenseData);
        await agtFinService.apiClient.post('/agtfin/expenses', expenseData);
      }
      
      // Recargar datos
      await loadExpensesData();
      
      // Limpiar formulario y cerrar modal
      resetForm();
      setOpenDialog(false);
      setEditingExpense(null);
      
      console.log('✅ Gasto guardado exitosamente');
      
    } catch (err) {
      console.error('❌ Error guardando gasto:', err);

      let errorMessage = editingExpense ? t('agtfin.expenses.messages.updateError') : t('agtfin.expenses.messages.createError');

      if (err.response?.status === 400) {
        errorMessage = err.response.data?.error || t('agtfin.expenses.messages.invalidData');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.expenses.messages.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Eliminar gasto
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm(t('agtfin.expenses.actions.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('🗑️ Eliminando gasto:', expenseId);
      
      await agtFinService.apiClient.delete(`/agtfin/expenses/${expenseId}`);
      
      // Recargar datos
      await loadExpensesData();
      
      console.log('✅ Gasto eliminado exitosamente');
      
    } catch (err) {
      console.error('❌ Error eliminando gasto:', err);

      let errorMessage = t('agtfin.expenses.messages.deleteError');

      if (err.response?.status === 404) {
        errorMessage = t('agtfin.expenses.messages.notFound');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.expenses.messages.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      category: 'other',
      description: '',
      paymentMethod: 'debit_card',
      isEssential: false
    });
    setFormErrors({});
  };

  // Abrir modal de edición
  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name || '',
      amount: expense.amount?.toString() || '',
      date: expense.date || format(new Date(), 'yyyy-MM-dd'),
      category: expense.category || 'other',
      description: expense.description || '',
      paymentMethod: expense.paymentMethod || 'debit_card',
      isEssential: Boolean(expense.isEssential)
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Abrir modal de detalles
  const openExpenseDetails = (expense) => {
    setSelectedExpense(expense);
    setOpenDetailsModal(true);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo si existe
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Filtrar gastos
  const getFilteredExpenses = () => {
    let filtered = expenses;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(expense => 
        expense.name?.toLowerCase().includes(term) ||
        expense.description?.toLowerCase().includes(term) ||
        expense.category?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  // Obtener ícono de categoría
  const getCategoryIcon = (category) => {
    const categoryData = EXPENSE_CATEGORIES.find(cat => cat.value === category);
    return categoryData?.icon || <CategoryIcon />;
  };

  // Obtener nombre de categoría
  const getCategoryName = (category) => {
    const categoryData = EXPENSE_CATEGORIES.find(cat => cat.value === category);
    return categoryData ? t(categoryData.labelKey) : category;
  };

  // Obtener nombre de método de pago
  const getPaymentMethodName = (method) => {
    const methodData = PAYMENT_METHODS.find(pm => pm.value === method);
    return methodData ? t(methodData.labelKey) : method;
  };

  // Formatear moneda
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    try {
      const date = parseLocalDate(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: es }) : dateString;
    } catch {
      return dateString;
    }
  };

  // Mostrar spinner mientras carga
  if (loading && expenses.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} sx={{ color: colors.primary }} />
        <Typography variant="body1" color="text.secondary">
          {t('agtfin.expenses.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <ModernContainer
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Encabezado moderno */}
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: '12px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingDownIcon sx={{ 
                fontSize: 24, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ color: colors.primary }}>
                {t('agtfin.expenses.title')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                {t('agtfin.expenses.subtitle')}
              </Typography>
            </Box>
          </Box>
          <ModernButton 
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            disabled={submitting}
          >
            {t('agtfin.expenses.new')}
          </ModernButton>
        </Box>

        {/* Mensaje de error */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              fontWeight: 500
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Sección de resumen */}
        <SummarySection
          summary={summary}
          userProfile={userProfile}
          theme={theme}
          t={t}
        />

        {/* Sección de categorías */}
        <CategorySummarySection
          summary={summary}
          theme={theme}
          t={t}
        />

        {/* Filtros modernos */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
            {t('agtfin.expenses.filtersAndSearch')}
          </Typography>
          
          <Box sx={{ 
            p: 3, 
            borderRadius: 4, 
            background: colors.backgrounds.card,
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)' 
              : '1px solid rgba(0,0,0,0.05)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.2)'
              : '0 8px 32px rgba(0,0,0,0.08)',
            display: 'flex', 
            gap: 3, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon sx={{ color: colors.primary, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.primary }}>
                {t('agtfin.expenses.search')}:
              </Typography>
            </Box>

            <ModernTextField
              noborder={true}
              size="small"
              placeholder={t('agtfin.expenses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200, flex: 1 }}
            />

            <ModernFormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('agtfin.expenses.form.category')}</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label={t('agtfin.expenses.form.category')}
              >
                <MenuItem value="">{t('agtfin.expenses.allCategories')}</MenuItem>
                {EXPENSE_CATEGORIES.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(category.icon, { fontSize: 'small' })}
                      {t(category.labelKey)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </ModernFormControl>
          </Box>
        </Box>

        {/* Lista de gastos */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
            {t('agtfin.expenses.list')}
          </Typography>
          
          {getFilteredExpenses().length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              background: colors.backgrounds.card,
              borderRadius: 4,
              border: theme.palette.mode === 'dark' 
                ? '1px solid rgba(255,255,255,0.1)' 
                : '1px solid rgba(0,0,0,0.05)',
            }}>
              <Box sx={{
                p: 3,
                borderRadius: '50%',
                background: colors.backgrounds.light,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}>
                <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {searchTerm || selectedCategory
                  ? t('agtfin.expenses.noExpensesFiltered')
                  : t('agtfin.expenses.empty')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {!searchTerm && !selectedCategory
                  ? t('agtfin.expenses.emptyHelp')
                  : t('agtfin.expenses.tryChangingFilters')}
              </Typography>
              {!searchTerm && !selectedCategory && (
                <ModernButton
                  startIcon={<AddIcon />}
                  onClick={() => setOpenDialog(true)}
                >
                  {t('agtfin.expenses.addFirst')}
                </ModernButton>
              )}
            </Box>
          ) : (
            <Box>
              {getFilteredExpenses().map((expense) => (
                <ModernExpenseCard key={expense.expenseId}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 3
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{
                            p: 1.5,
                            borderRadius: '12px',
                            background: colors.backgrounds.medium,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {React.cloneElement(getCategoryIcon(expense.category), { 
                              sx: { fontSize: 20, color: colors.primary } 
                            })}
                          </Box>
                          <Box>
                            <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary }}>
                              {expense.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              {getCategoryName(expense.category)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                          -{formatCurrency(expense.amount)}
                        </Typography>
                        <ModernChip
                          label={expense.isEssential ? t('agtfin.expenses.form.essentialYes') : t('agtfin.expenses.form.essentialNo')}
                          chiptype={expense.isEssential ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 3, 
                      mb: 3,
                      flexWrap: 'wrap'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {formatDate(expense.date)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {getPaymentMethodName(expense.paymentMethod)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {expense.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                        {expense.description}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 3, backgroundColor: colors.backgrounds.medium }} />
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Tooltip title={t('agtfin.expenses.actions.viewDetails')} arrow>
                        <IconButton
                          size="small"
                          onClick={() => openExpenseDetails(expense)}
                          sx={{
                            borderRadius: 2,
                            background: colors.backgrounds.light,
                            '&:hover': {
                              background: colors.backgrounds.medium,
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <VisibilityIcon fontSize="small" sx={{ color: colors.primary }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={t('agtfin.expenses.actions.edit')} arrow>
                        <IconButton
                          size="small"
                          onClick={() => openEditExpense(expense)}
                          disabled={submitting}
                          sx={{
                            borderRadius: 2,
                            background: colors.backgrounds.light,
                            '&:hover': {
                              background: colors.backgrounds.medium,
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <EditIcon fontSize="small" sx={{ color: colors.primary }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={t('agtfin.expenses.actions.delete')} arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteExpense(expense.expenseId)}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            background: colors.backgrounds.light,
                            '&:hover': {
                              background: colors.backgrounds.medium,
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <DeleteIcon fontSize="small" sx={{ color: colors.tertiary }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </ModernExpenseCard>
              ))}
            </Box>
          )}
        </Box>

        {/* Modal de crear/editar gasto */}
        <ModernDialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {editingExpense ? (
                <EditIcon sx={{ 
                  fontSize: 20, 
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                }} />
              ) : (
                <AddIcon sx={{ 
                  fontSize: 20, 
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                }} />
              )}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {editingExpense ? t('agtfin.expenses.form.editTitle') : t('agtfin.expenses.new')}
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.expenses.form.name')}
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  placeholder={t('agtfin.expenses.form.namePlaceholder')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.expenses.form.amount')}
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                  InputProps={{
                    startAdornment: (
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ mr: 1, fontWeight: 600 }}
                      >
                        {userProfile?.currency === 'USD' ? '$' : 
                         userProfile?.currency === 'EUR' ? '€' : 
                         userProfile?.currency === 'CLP' ? '$' : 
                         userProfile?.currency || '$'}
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.expenses.form.date')}
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleFormChange('date', e.target.value)}
                  error={!!formErrors.date}
                  helperText={formErrors.date}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ModernFormControl fullWidth>
                  <InputLabel>{t('agtfin.expenses.form.category')}</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    label={t('agtfin.expenses.form.category')}
                  >
                    {EXPENSE_CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {React.cloneElement(category.icon, { fontSize: 'small' })}
                          {t(category.labelKey)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </ModernFormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ModernFormControl fullWidth>
                  <InputLabel>{t('agtfin.expenses.form.paymentMethod')}</InputLabel>
                  <Select
                    value={formData.paymentMethod}
                    onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                    label={t('agtfin.expenses.form.paymentMethod')}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {t(method.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </ModernFormControl>
              </Grid>

              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.expenses.form.description')}
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder={t('agtfin.expenses.form.descriptionPlaceholder')}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isEssential}
                      onChange={(e) => handleFormChange('isEssential', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: colors.primary,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: colors.secondary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight={500}>
                      {t('agtfin.expenses.form.essential')}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            gap: 2
          }}>
            <Button
              onClick={() => setOpenDialog(false)}
              disabled={submitting}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.expenses.form.cancel')}
            </Button>
            <ModernButton
              onClick={handleSubmit}
              disabled={!formData.name || !formData.amount || submitting}
              startIcon={submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                editingExpense ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />
              )}
            >
              {submitting
                ? (editingExpense ? t('agtfin.expenses.form.updating') : t('agtfin.expenses.form.saving'))
                : (editingExpense ? t('agtfin.expenses.form.update') : t('agtfin.expenses.form.create'))
              }
            </ModernButton>
          </DialogActions>
        </ModernDialog>

        {/* Modal de detalles de gasto */}
        <ModernDialog 
          open={openDetailsModal} 
          onClose={() => setOpenDetailsModal(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <VisibilityIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {t('agtfin.expenses.details.title')}
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            {selectedExpense && (
              <Grid container spacing={4}>
                {/* Información principal */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ 
                    p: 4, 
                    borderRadius: 4,
                    background: colors.backgrounds.card,
                    border: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255,255,255,0.1)' 
                      : '1px solid rgba(0,0,0,0.05)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
                      <Box sx={{
                        p: 2,
                        borderRadius: '16px',
                        background: colors.gradients.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {React.cloneElement(getCategoryIcon(selectedExpense.category), { 
                          sx: { 
                            fontSize: 32, 
                            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                          } 
                        })}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                          {selectedExpense.name}
                        </Typography>
                        <Typography variant="h5" fontWeight={600} sx={{ color: colors.secondary }}>
                          -{formatCurrency(selectedExpense.amount)}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.expenses.details.category')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                            {getCategoryName(selectedExpense.category)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.expenses.details.type')}
                          </Typography>
                          <ModernChip
                            label={selectedExpense.isEssential ? t('agtfin.expenses.form.essentialYes') : t('agtfin.expenses.form.essentialNo')}
                            chiptype={selectedExpense.isEssential ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.expenses.details.date')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                              {formatDate(selectedExpense.date)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.expenses.details.paymentMethod')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                              {getPaymentMethodName(selectedExpense.paymentMethod)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.expenses.details.registrationDate')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                            {selectedExpense.createdAt ? format(new Date(selectedExpense.createdAt), 'dd/MM/yyyy HH:mm') : t('agtfin.common.notAvailable')}
                          </Typography>
                        </Box>
                      </Grid>

                      {selectedExpense.description && (
                        <Grid item xs={12}>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                              {t('agtfin.expenses.details.description')}
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              color: colors.primary,
                              fontStyle: 'italic',
                              background: colors.backgrounds.light,
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${colors.backgrounds.medium}`
                            }}>
                              {selectedExpense.description}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Panel lateral con información adicional */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 4,
                    background: colors.backgrounds.card,
                    border: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255,255,255,0.1)' 
                      : '1px solid rgba(0,0,0,0.05)',
                    height: 'fit-content'
                  }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: colors.primary }}>
                      {t('agtfin.expenses.details.additionalInfo')}
                    </Typography>

                    {/* Análisis del gasto */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                        {t('agtfin.expenses.details.analysis')}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('agtfin.expenses.details.percentOfTotal')}
                        </Typography>
                        {(() => {
                          const monthlyTotal = summary.total || 1;
                          const percentage = (selectedExpense.amount / monthlyTotal) * 100;
                          
                          return (
                            <Box>
                              <Box sx={{ 
                                height: '6px', 
                                backgroundColor: colors.backgrounds.medium,
                                borderRadius: '3px',
                                overflow: 'hidden',
                                mb: 1
                              }}>
                                <Box sx={{
                                  height: '100%',
                                  width: `${Math.min(percentage, 100)}%`,
                                  background: colors.gradients.primary,
                                  transition: 'width 0.3s ease'
                                }} />
                              </Box>
                              <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary }}>
                                {percentage.toFixed(1)}%
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('agtfin.expenses.details.categoryLabel')}
                        </Typography>
                        <ModernChip
                          label={getCategoryName(selectedExpense.category)}
                          chiptype="secondary"
                          size="small"
                        />
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('agtfin.expenses.details.importance')}
                        </Typography>
                        <ModernChip
                          label={selectedExpense.isEssential ? t('agtfin.expenses.details.essentialExpense') : t('agtfin.expenses.details.optionalExpense')}
                          chiptype={selectedExpense.isEssential ? 'primary' : 'tertiary'}
                          size="small"
                        />
                      </Box>
                    </Box>

                    {/* Comparación temporal */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                        {t('agtfin.expenses.details.comparison')}
                      </Typography>
                      
                      {(() => {
                        const avgExpense = summary.count > 0 ? (summary.total / summary.count) : 0;
                        const difference = selectedExpense.amount - avgExpense;
                        const isAboveAverage = difference > 0;
                        
                        return (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              {isAboveAverage ? (
                                <TrendingUpIcon sx={{ fontSize: 16, color: colors.tertiary }} />
                              ) : (
                                <TrendingDownIcon sx={{ fontSize: 16, color: colors.secondary }} />
                              )}
                              <Typography variant="body2" fontWeight={500} sx={{ color: colors.primary }}>
                                {isAboveAverage ? t('agtfin.expenses.details.above') : t('agtfin.expenses.details.below')} {t('agtfin.expenses.details.theAverage')}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('agtfin.expenses.details.average')} {formatCurrency(avgExpense)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('agtfin.expenses.details.difference')} {isAboveAverage ? '+' : ''}{formatCurrency(difference)}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    {/* Acciones rápidas */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                        {t('agtfin.expenses.details.quickActions')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <ModernButton
                          fullWidth
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setOpenDetailsModal(false);
                            openEditExpense(selectedExpense);
                          }}
                          disabled={loading}
                          size="small"
                        >
                          {t('agtfin.expenses.details.editExpense')}
                        </ModernButton>

                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setOpenDetailsModal(false);
                            handleDeleteExpense(selectedExpense.expenseId);
                          }}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: colors.backgrounds.dark,
                            color: colors.tertiary,
                            '&:hover': {
                              borderColor: colors.tertiary,
                              background: colors.backgrounds.light
                            }
                          }}
                          size="small"
                        >
                          {t('agtfin.expenses.details.deleteExpense')}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            justifyContent: 'flex-end'
          }}>
            <Button 
              onClick={() => setOpenDetailsModal(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.common.close')}
            </Button>
          </DialogActions>
        </ModernDialog>
      </ModernContainer>
    </LocalizationProvider>
  );
};

export default FinExpense;
